import { Component, h, Prop, Host, Element, Event, EventEmitter, State } from '@stencil/core';
import classcat from 'classcat';

export interface AhaComment extends HTMLAhaCommentElement {}

@Component({
  tag: 'aha-comment',
  styleUrl: 'aha-comment.css',
  shadow: true,
})
export class AhaComment {
  @Element() host!: HTMLAhaCommentElement;

  /** The date of the comment. */
  @Prop() commentDate?: string;

  /** The number of replies to the comment. */
  @Prop() replyCount?: number;

  /** Whether to show the likes button */
  @Prop() showLikes?: boolean;

  /** The number of likes on the comment. */
  @Prop() likesCount?: number;

  /** Whether to show the leading and trailing lines. */
  @Prop() isChild: boolean = false;

  /** Whether to show the leading and trailing lines. */
  @Prop() defaultShowReplies: boolean = false;

  /** Whether to show the leading and trailing lines. */
  @Prop() repliesEnabled: boolean = false;

  /** Whether this comment is an annotation and has slightly different styling */
  @Prop() isAnnotation: boolean = false;

  /** Whether replies are currently shown */
  @State() repliesShown: boolean = false;

  /**
   * Triggers when the show reply button is clicked.
   */
  @Event({
    eventName: 'aha-comment:show-replies',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  showRepliesEvent!: EventEmitter;

  /**
   * Triggers when the comment is liked.
   */
  @Event({
    eventName: 'aha-comment:like',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  likeEvent!: EventEmitter;

  componentWillLoad() {
    this.repliesShown = this.defaultShowReplies;
  }

  componentDidLoad() {
    // get the replies element
    const shadow = this.host.shadowRoot;
    if (shadow) {
      const repliesElement = shadow.querySelector('.aha-comment__replies');
      const shareControls: HTMLElement | null = shadow.querySelector(
        '.aha-comment__share-controls-hover',
      );
      if (shareControls && repliesElement) {
        const showControls = (shouldShow: boolean) => {
          shareControls.classList.add(shouldShow ? 'show' : 'hide');
          shareControls.classList.remove(shouldShow ? 'hide' : 'show');
        };
        this.host.addEventListener('mouseenter', () => showControls(true));
        this.host.addEventListener('mouseleave', () => showControls(false));
        repliesElement.addEventListener('mouseenter', () => showControls(false));
        repliesElement.addEventListener('mouseleave', () => showControls(true));
      }
    }
  }

  private handleToggleReplies(event: Event) {
    event.preventDefault();
    this.repliesShown = !this.repliesShown;
    const target = event.target as HTMLElement;
    const shouldFocus = target.getAttribute('aria-label') === 'Reply';

    this.showRepliesEvent.emit({ repliesShown: this.repliesShown, shouldFocus });
  }

  private handleLike(event: Event) {
    this.likeEvent.emit(event);
  }

  render() {
    return (
      <Host>
        <div
          class={classcat({
            'aha-comment__container': true,
            'aha-comment__container--annotation': this.isAnnotation,
            'aha-comment__container--child': this.isChild,
          })}
        >
          <div
            class={classcat({
              'aha-comment__avatar-container': true,
              'aha-comment__avatar-container--child': this.isChild,
            })}
          >
            {this.isChild && <div class="line" />}
            <div class={'aha-comment__avatar'}>
              <slot name="avatar" />
            </div>
            {this.isChild && <div class="line" />}
          </div>
          <div
            class={classcat({
              'aha-comment__content': true,
              'aha-comment__content--child': this.isChild,
            })}
          >
            <div class="aha-comment__header">
              <span class="aha-comment__name">
                <slot name="commenter-name"></slot>
              </span>
              <div class="aha-comment__share-controls">
                <div class="aha-comment__share-controls-hover hide">
                  {this.repliesEnabled && !this.isChild && (
                    <aha-comment-button line>
                      <span aria-label="Reply" onClick={e => this.handleToggleReplies(e)}>
                        Reply
                      </span>
                    </aha-comment-button>
                  )}
                  <slot name="share-controls" />
                </div>
                <span class="aha-comment__share-controls-date">
                  {this.commentDate && <aha-date date={this.commentDate} format="medium" />}
                </span>
                <aha-tooltip placement="bottom">
                  {this.commentDate && (
                    <aha-date date={this.commentDate} format="medium" timeFormat="short" />
                  )}
                </aha-tooltip>
              </div>
            </div>
            <slot name="body" />
            {!!this.replyCount || !!this.showLikes ? (
              <div class="aha-comment__controls">
                {!!this.replyCount && (
                  <a aria-label="Toggle replies" onClick={e => this.handleToggleReplies(e)}>
                    <fa-icon
                      class={`fa-solid ${
                        this.repliesShown ? 'fa-arrow-turn-left-up' : 'fa-arrow-turn-down-right'
                      }`}
                    ></fa-icon>
                    {!this.repliesShown ? `${this.replyCount} replies` : 'Hide replies'}
                  </a>
                )}
                {!!this.showLikes && (
                  <a onClick={e => this.handleLike(e)}>
                    <fa-icon class="fa-solid fa-thumbs-up"></fa-icon>
                    {this.likesCount === 0 || this.likesCount === undefined
                      ? 'Like'
                      : this.likesCount === 1
                      ? '1 Like'
                      : `${this.likesCount} Likes`}
                  </a>
                )}
              </div>
            ) : null}

            <div class="aha-comment__replies">{this.repliesShown && <slot name="replies" />}</div>
          </div>
        </div>
      </Host>
    );
  }
}
